import { useAuth } from "@/_core/hooks/useAuth";
import DashboardLayout from "@/components/DashboardLayout";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { trpc } from "@/lib/trpc";
import {
  Users,
  Apple,
  Utensils,
  Scale,
  Dumbbell,
  TrendingUp,
} from "lucide-react";
import { Link } from "wouter";
import { useTranslation } from "@/hooks/useTranslation";

export default function Home() {
  const { user } = useAuth();
  const { data: klienti, isLoading: klientiLoading } =
    trpc.klient.list.useQuery();
  const { data: potraviny, isLoading: potravinyLoading } =
    trpc.potravina.list.useQuery();
  const { t } = useTranslation();

  // Debug the user data
  console.log("User data in Home component:", user);

  const stats = [
    {
      title: t("stats.totalClients", "Total Clients"),
      value: klientiLoading ? "..." : klienti?.length || 0,
      icon: Users,
      href: "/clients",
      description: t("activeClientProfiles", "Active client profiles"),
      color: "text-blue-600",
      bgColor: "bg-blue-50",
    },
    {
      title: t("stats.foodDatabase", "Food Database"),
      value: potravinyLoading ? "..." : potraviny?.length || 0,
      icon: Apple,
      href: "/foods",
      description: t("foodsWithNutritionData", "Foods with nutrition data"),
      color: "text-green-600",
      bgColor: "bg-green-50",
    },
    {
      title: t("stats.dailyTracking", "Daily Tracking"),
      icon: Utensils,
      href: "/intake",
      description: t("trackClientFoodIntake", "Track client food intake"),
      color: "text-orange-600",
      bgColor: "bg-orange-50",
    },
    {
      title: t("stats.measurements", "Measurements"),
      icon: Scale,
      href: "/measurements",
      description: t("bodyCompositionTracking", "Body composition tracking"),
      color: "text-purple-600",
      bgColor: "bg-purple-50",
    },
    {
      title: t("stats.attendance", "Attendance"),
      icon: Dumbbell,
      href: "/attendance",
      description: t("trainingSessionRecords", "Training session records"),
      color: "text-red-600",
      bgColor: "bg-red-50",
    },
    {
      title: t("stats.progress", "Progress"),
      icon: TrendingUp,
      href: "/strength-progress",
      description: t("clientProgressReports", "Client progress reports"),
      color: "text-indigo-600",
      bgColor: "bg-indigo-50",
    },
  ];

  return (
    <DashboardLayout>
      <div className="container py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">
            {user?.name
              ? t("home:welcome", {
                  defaultValue: "Welcome back, {name}!",
                  name: user.name,
                })
              : t("home:welcome", { defaultValue: "Welcome back, Trainer!" })}
          </h1>
          <p className="text-muted-foreground">
            {t(
              "subtitle",
              "Manage your clients' nutrition and fitness tracking from one place."
            )}
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {stats.map(stat => (
            <Link key={stat.title} href={stat.href}>
              <Card className="hover:shadow-lg transition-shadow cursor-pointer">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    {stat.title}
                  </CardTitle>
                  <div className={`p-2 rounded-lg ${stat.bgColor}`}>
                    <stat.icon className={`h-5 w-5 ${stat.color}`} />
                  </div>
                </CardHeader>
                <CardContent>
                  {stat.value !== undefined && (
                    <div className="text-3xl font-bold mb-1">{stat.value}</div>
                  )}
                  <p className="text-xs text-muted-foreground">
                    {stat.description}
                  </p>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>

        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <CardTitle>{t("quickActions.title", "Quick Actions")}</CardTitle>
            <CardDescription>
              {t("quickActions.description", "Common tasks to get started")}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Link
                href="/clients"
                className="flex items-center gap-3 p-4 rounded-lg border border-border hover:bg-muted transition-colors"
              >
                <Users className="h-5 w-5 text-primary" />
                <div>
                  <div className="font-medium">
                    {t("quickActions.manageClients", "Manage Clients")}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {t(
                      "quickActions.manageClientsDescription",
                      "Add or edit client profiles"
                    )}
                  </div>
                </div>
              </Link>

              <Link
                href="/intake"
                className="flex items-center gap-3 p-4 rounded-lg border border-border hover:bg-muted transition-colors"
              >
                <Utensils className="h-5 w-5 text-primary" />
                <div>
                  <div className="font-medium">
                    {t("quickActions.logFoodIntake", "Log Food Intake")}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {t(
                      "quickActions.logFoodIntakeDescription",
                      "Track daily nutrition"
                    )}
                  </div>
                </div>
              </Link>

              <Link
                href="/measurements"
                className="flex items-center gap-3 p-4 rounded-lg border border-border hover:bg-muted transition-colors"
              >
                <Scale className="h-5 w-5 text-primary" />
                <div>
                  <div className="font-medium">
                    {t(
                      "quickActions.recordMeasurements",
                      "Record Measurements"
                    )}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {t(
                      "quickActions.recordMeasurementsDescription",
                      "Log body composition data"
                    )}
                  </div>
                </div>
              </Link>

              <Link
                href="/attendance"
                className="flex items-center gap-3 p-4 rounded-lg border border-border hover:bg-muted transition-colors"
              >
                <Dumbbell className="h-5 w-5 text-primary" />
                <div>
                  <div className="font-medium">
                    {t(
                      "quickActions.logTrainingSession",
                      "Log Training Session"
                    )}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {t(
                      "quickActions.logTrainingSessionDescription",
                      "Record attendance and pricing"
                    )}
                  </div>
                </div>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
